/**
 * API client with authentication
 */
import { config } from '../utils/config';
import { storage, STORAGE_KEYS } from '../utils/storage';

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    // Load token from storage on init
    this.token = storage.get<string>(STORAGE_KEYS.AUTH_TOKEN);
  }

  /**
   * Set auth token
   */
  setToken(token: string) {
    this.token = token;
    storage.set(STORAGE_KEYS.AUTH_TOKEN, token);
  }

  /**
   * Clear auth token
   */
  clearToken() {
    this.token = null;
    storage.remove(STORAGE_KEYS.AUTH_TOKEN);
  }

  /**
   * Get current token
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Check if authenticated
   */
  isAuthenticated(): boolean {
    return !!this.token;
  }

  /**
   * Make HTTP request
   */
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    // Fastify rejects requests carrying Content-Type: application/json with an empty body
    // (400 FST_ERR_CTP_EMPTY_JSON_BODY), so the header is only set when a body exists.
    const headers: Record<string, string> = {
      ...(options.body != null ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers as Record<string, string>),
    };

    // Add auth token if available
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    // Add dev user ID header if in dev mode
    if (!import.meta.env.PROD && config.devAuth && config.devUserId) {
      headers['X-Dev-User-Id'] = config.devUserId.toString();
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Handle auth errors
      if (response.status === 401) {
        this.clearToken();
        throw new ApiError('Unauthorized', response.status);
      }

      // Handle 403 - user may have been disabled or role changed
      if (response.status === 403) {
        this.clearToken();
        // Show user-friendly message
        if (typeof window !== 'undefined') {
          // Force reload to clear state and redirect to login
          setTimeout(() => {
            window.location.href = '/brands';
          }, 100);
        }
        throw new ApiError(
          'Access revoked. Your permissions may have changed. Please re-authenticate.',
          response.status,
        );
      }

      // Parse response
      let data: unknown;
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        throw new ApiError(`Request failed: ${response.statusText}`, response.status, data);
      }

      return data as T;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(error instanceof Error ? error.message : 'Network error', 0);
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, params?: Record<string, unknown>): Promise<T> {
    const queryString = params
      ? '?' +
        new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null)
            .map(([k, v]) => [k, String(v)]),
        ).toString()
      : '';

    return this.request<T>(`${endpoint}${queryString}`, {
      method: 'GET',
    });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }
}

// Export singleton instance
export const apiClient = new ApiClient(config.apiBaseUrl);

export { ApiError };
